import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Alert,
  CircularProgress,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PromptSelector from '../components/PromptSelector';
import GenerationStatusCard from '../components/GenerationStatusCard';
import ErrorState from '../components/ErrorState';
import {
  getLookPrompts,
  generateAvatarLook,
  getLookStatus,
  getAvatarLooks,
} from '../../../apis/apiList/publicAiStudioApi';

const CreateLookStep = ({ 
  selectedAvatar, 
  selectedLook,
  selectedLookId,
  onSelectLook,
  generatedLook,
  onGeneratedLook,
}) => {
  const [prompts] = useState(getLookPrompts());
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [promptText, setPromptText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationId, setGenerationId] = useState(null);
  const [error, setError] = useState(null);
  const [currentMessage, setCurrentMessage] = useState('');
  const [existingLooks, setExistingLooks] = useState([]);
  const [loadingLooks, setLoadingLooks] = useState(false);

  const pollingIntervalRef = React.useRef(null);
  const messageTimeoutRef = React.useRef(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Fetch existing looks when avatar changes
  useEffect(() => {
    if (selectedAvatar) {
      fetchExistingLooks();
    }
  }, [selectedAvatar]);

  const fetchExistingLooks = async () => {
    try {
      setLoadingLooks(true);
      const avatarId = selectedAvatar.groupId || selectedAvatar._id;
      const response = await getAvatarLooks(avatarId);
      const looksData = Array.isArray(response) ? response : response?.data || [];
      const filteredLooks = looksData.filter((look) =>
        !selectedAvatar.style || look.style === selectedAvatar.style
      );
      setExistingLooks(filteredLooks);
    } catch (err) {
      console.error('Failed to fetch existing looks:', err);
      setExistingLooks([]);
    } finally {
      setLoadingLooks(false);
    }
  };

  const handlePromptSelect = (value) => {
    setSelectedPrompt(value);
    setPromptText(value);
  };

  const handleGenerateLook = async () => {
    if (!promptText || promptText.length < 10) {
      setError('Please enter at least 10 characters for the prompt');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setGenerationStatus('queued');
      const avatarGroupId = selectedAvatar.avatarGroupId || selectedAvatar.groupId || selectedAvatar.heygenAvatarId;
      const lookName = `${selectedAvatar.groupName} Look - ${new Date().toISOString()}`;
      const lookType = 'public-avatar-look';
      const lookState = 'public-avatar-look';
      const avatarType = selectedAvatar.style === 'animated' ? 'animated' : selectedAvatar.style || 'realistic';
      const selectedPromptObject = prompts.find((prompt) => prompt.value === selectedPrompt);
      const attirePrompt = selectedPromptObject?.attire || 'wearing role-appropriate attire.';
      const promptWithType = `${promptText.trim()} ${attirePrompt}`;
      const referenceImagePath = selectedAvatar.style === 'animated'
        ? selectedAvatar.cartoonImage || selectedAvatar.image
        : selectedAvatar.image;

      const response = await generateAvatarLook({
        avatarId: selectedAvatar.groupId || selectedAvatar._id,
        avatarHeygenId: selectedAvatar.heygenAvatarId || selectedAvatar.avatarId || selectedAvatar.cartoonAvatarId,
        avatarGroupId,
        role: selectedPromptLabel,
        prompt: promptWithType,
        style: selectedAvatar.style || 'realistic',
        avatarType,
        type: lookType,
        name: lookName,
        state: lookState,
        referenceImagePath: `${referenceImagePath}`,
      });
      console.log('Generate look response:', response);

      const genId = response.generationId || response.data?.generationId || response.lookId;
      setGenerationId(genId);
      startPolling(genId);
    } catch (err) {
      console.error('Failed to generate look:', err);
      setError(err.response?.data?.message || 'Failed to generate look');
      setGenerating(false);
      setGenerationStatus(null);
    }
  };

  const startPolling = (genId) => {
    setGenerationStatus('processing');
    setCurrentMessage('Analyzing your prompt...');

    messageTimeoutRef.current = setTimeout(() => {
      setCurrentMessage('Applying styling...');
    }, 15000);

    messageTimeoutRef.current = setTimeout(() => {
      setCurrentMessage('Creating avatar look...');
    }, 30000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await getLookStatus(genId);
        const status = statusResponse.status || statusResponse.data?.status;

        if (status === 'completed') {
          clearInterval(pollingIntervalRef.current);
          if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
          }
          setGenerationStatus(status);
          setGenerating(false);
          
          const lookData = {
            id: genId,
            image: statusResponse.imageUrl || statusResponse.data?.imageUrl,
            name: statusResponse.name || statusResponse.data?.name,
            prompt: promptText,
            status,
            ...statusResponse.data,
          };
          onGeneratedLook(lookData);
          handleSelectLook('generated', lookData);
          // Refresh existing looks to include the new one
          fetchExistingLooks();
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollingIntervalRef.current);
          if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
          }
          setGenerationStatus(status);
          setGenerating(false);
          setError('Look generation failed. Please try again.');
        } else {
          setGenerationStatus(status || 'processing');
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollingIntervalRef.current);
        if (messageTimeoutRef.current) {
          clearTimeout(messageTimeoutRef.current);
        }
        setGenerationStatus('failed');
        setGenerating(false);
        setError('Failed to check generation status');
      }
    }, 5000);
  };

  const handleSelectLook = (lookType, lookData = null) => {
    onSelectLook(lookType, lookData);
  };

  const selectedPromptObject = prompts.find((prompt) => prompt.value === selectedPrompt);
  const selectedPromptLabel = selectedPromptObject?.label || 'Custom Prompt';
  const isPromptValid = promptText.length >= 20 && promptText.length <= 250;

  const pendingLook = generating ? [{
    type: 'pending',
    data: {
      generationId: generationId || 'pending',
      image: selectedAvatar?.image,
      prompt: promptText,
    },
  }] : [];

  const existingLookIds = new Set(existingLooks.map((look) => look.generationId || look.id));
  const generatedLookId = generatedLook?.generationId || generatedLook?.id;
  const generatedLookEntry = generatedLook && generatedLook.status === 'completed' && !existingLookIds.has(generatedLookId)
    ? [{ type: 'generated', data: generatedLook }]
    : [];

  // Combine all available looks with a pending generation card
  const allLooks = [
    { type: 'original', data: selectedAvatar },
    ...pendingLook,
    ...generatedLookEntry,
    ...existingLooks.map(look => ({ type: 'existing', data: look })),
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Select or Create Avatar Look
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Choose from existing looks or generate a new look for your selected avatar.
      </Typography>

      <Card sx={{ mb: 3, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          <Card
            sx={{
              minWidth: 260,
              width: 260,
              p: 0,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height="320"
                image={`${selectedAvatar.image}`}
                alt={selectedAvatar.groupName}
                sx={{ objectFit: 'cover',
                  height: {
                    xs: 230,
                    sm: 230,
                    md: 270,
                  }
                 }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  letterSpacing: 0.5,
                }}
              >
                Base Look
              </Box>
            </Box>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {selectedAvatar.groupName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedAvatar.style} • {selectedAvatar.gender} • {selectedAvatar.accent}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the input box to describe edits you'd like for this look.
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Generate New Look
            </Typography>
            <PromptSelector
              prompts={prompts}
              selectedPrompt={selectedPrompt}
              onSelect={handlePromptSelect}
              label="Choose a Look Prompt"
              helperText="Select a Prompt or write your own custom prompt below"
            />
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Describe Your Avatar Look"
              value={promptText}
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  setPromptText(e.target.value);
                }
              }}
              placeholder="Describe the setting, clothing, lighting, and style you want for your avatar..."
              error={promptText.length > 0 && promptText.length < 20}
              helperText={
                promptText.length > 0 && promptText.length < 20
                  ? `${20 - promptText.length} characters needed (minimum 20)`
                  : `${promptText.length}/250 characters`
              }
              sx={{ mb: 3 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleGenerateLook}
              disabled={!isPromptValid || generating}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {generating ? 'Generating...' : 'Create New Look'}
            </Button>
          </Box>
        </Box>
      </Card>

      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Available Looks
        </Typography>
        {loadingLooks ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {allLooks.map((look, index) => {
              const isOriginal = look.type === 'original';
              const isPending = look.type === 'pending';
              const isGenerated = look.type === 'generated';
              const isExisting = look.type === 'existing';
              const lookData = look.data;
              const imageUrl = isOriginal ? lookData.image : lookData.imageUrl || lookData.image;
              const title = isOriginal ? 'Original Avatar' : isPending ? 'Generating Look' : isGenerated ? 'Generated Look' : 'Generated Look';
              const description = isOriginal
                ? lookData.groupName
                : (lookData.prompt?.substring(0, 60) + '...' || 'Custom look');
              const lookId = isOriginal ? 'original' : lookData.generationId || lookData.id;
              const isSelected = selectedLookId === lookId;

              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    onClick={() => handleSelectLook(
                      isOriginal ? 'original' : isPending ? 'pending' : 'existing',
                      isOriginal || isPending ? null : lookData
                    )}
                    sx={{
                      cursor: isPending ? 'default' : 'pointer',
                      border: '2px solid',
                      borderColor: isSelected ? '#DC2626' : 'transparent',
                      transition: 'all 0.3s',
                      position: 'relative',
                      height: '100%',
                      '&:hover': {
                        borderColor: isPending ? 'transparent' : '#DC2626',
                        transform: isPending ? 'none' : 'scale(1.02)',
                      },
                    }}
                  >
                    {isSelected && !isPending && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: '#DC2626',
                          borderRadius: '50%',
                          p: 0.5,
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    )}
                    <CardMedia
                      component="img"
                      height="180"
                      image={`${imageUrl}`}
                      alt={title}
                      sx={{ objectFit: 'cover',
                        height: {
                          xs: 230,
                          sm: 230,
                          md: 450,
                        }
                      }}
                    />
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {title} {!isOriginal && !isPending && '✨'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {description}
                      </Typography>
                    </CardContent>
                    {isPending && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.64)',
                          color: '#fff',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          p: 2,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#fff' }}>
                          {currentMessage || 'Creating avatar look...'}
                        </Typography>
                        <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.9)' }} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      
      {!selectedLook && !generating && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Please select one of the avatar looks above to continue
        </Alert>
      )}
    </Box>
  );
};

export default CreateLookStep;
